/**
 * The shared close cross: markup only, the look is `styles/close.css.ts`.
 *
 * `createCloseButton` for windows built with the DOM, `closeButtonHtml` for the
 * ones that render an innerHTML template. Both emit the same element: a
 * type=button, 44px hit area, aria-label (default "Close"), class `bv-close`
 * plus any extra class the window uses as its own hook.
 */
export interface CloseButtonOptions {
  onClick?: () => void;
  /** aria-label, e.g. "Close shop". Defaults to "Close". */
  label?: string;
  /** Extra classes beside bv-close: the window's own hook and placement. */
  className?: string;
}

const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

export function createCloseButton(opts: CloseButtonOptions = {}): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = opts.className ? `bv-close ${opts.className}` : 'bv-close';
  btn.setAttribute('aria-label', opts.label ?? 'Close');
  if (opts.onClick) {
    const fn = opts.onClick;
    btn.addEventListener('click', () => fn());
  }
  return btn;
}

/** The same button as a markup string; `attrs` is raw, trusted attribute text (e.g. data-close). */
export function closeButtonHtml(opts: Omit<CloseButtonOptions, 'onClick'> & { attrs?: string } = {}): string {
  const cls = opts.className ? `bv-close ${esc(opts.className)}` : 'bv-close';
  return `<button type="button" class="${cls}" aria-label="${esc(opts.label ?? 'Close')}"${opts.attrs ? ` ${opts.attrs}` : ''}></button>`;
}
